import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Phone, Mail, MapPin, Send, X } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const EnquiryModal = ({ isOpen, onClose, enquiryType = "general" }) => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    careType: "",
    urgency: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getTitle = () => {
    switch (enquiryType) {
      case "visit":
        return "Book a Visit";
      case "brochure":
        return "Request a Brochure";
      case "speak":
        return "Speak to Our Team";
      default:
        return "Make an Enquiry";
    }
  };

  const getDefaultMessage = () => {
    switch (enquiryType) {
      case "visit":
        return "I would like to arrange a visit to Barnston Court Nursing Home.";
      case "brochure":
        return "Please send me a brochure with more information about Barnston Court Nursing Home.";
      case "speak":
        return "I would like to speak with someone from your team about care options.";
      default:
        return "";
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact/enquiry`, {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        care_type: formData.careType,
        urgency: formData.urgency,
        message: formData.message || getDefaultMessage(),
        enquiry_type: enquiryType
      });

      if (response.status === 201) {
        toast.success("Thank you for your enquiry! We'll be in touch within 24 hours.");
        setFormData({
          name: "",
          phone: "",
          email: "",
          careType: "",
          urgency: "",
          message: ""
        });
        onClose();
      }
    } catch (error) {
      console.error("Error submitting enquiry:", error);
      toast.error("Something went wrong. Please try calling us directly at 0151 677 5573.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto"
        data-testid="enquiry-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-teal-700 flex items-center gap-2">
            {getTitle()}
          </DialogTitle>
        </DialogHeader>

        {/* Quick Contact Info */}
        <div className="bg-teal-50 rounded-lg p-4 mb-4">
          <p className="text-sm text-teal-700 font-medium mb-2">Prefer to talk? Contact us directly:</p>
          <div className="flex flex-wrap gap-4 text-sm">
            <a 
              href="tel:01516775573" 
              className="flex items-center gap-1 text-teal-700 hover:text-teal-800"
              data-testid="modal-phone-link"
            >
              <Phone className="h-4 w-4" />
              0151 677 5573
            </a>
            <a 
              href="mailto:manager@barnstoncourt.com" 
              className="flex items-center gap-1 text-teal-700 hover:text-teal-800"
              data-testid="modal-email-link"
            >
              <Mail className="h-4 w-4" />
              manager@barnstoncourt.com
            </a>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <Label htmlFor="modal-name">Your Name *</Label>
            <Input
              id="modal-name"
              data-testid="enquiry-name-input"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              placeholder="John Smith"
              required
              className="mt-1"
            />
          </div>

          {/* Phone & Email Row */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="modal-phone">Phone Number *</Label>
              <Input
                id="modal-phone"
                data-testid="enquiry-phone-input"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChange("phone", e.target.value)}
                placeholder="0151 234 5678"
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="modal-email">Email Address *</Label>
              <Input
                id="modal-email"
                data-testid="enquiry-email-input"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                placeholder="john@example.com"
                required
                className="mt-1"
              />
            </div>
          </div>

          {/* Care Type & Urgency Row */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="modal-careType">Type of Care Needed</Label>
              <Select
                value={formData.careType}
                onValueChange={(value) => handleChange("careType", value)}
              >
                <SelectTrigger className="mt-1" data-testid="enquiry-care-type-select">
                  <SelectValue placeholder="Select care type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="residential">Residential Care</SelectItem>
                  <SelectItem value="nursing">Nursing Care</SelectItem>
                  <SelectItem value="dementia">Dementia Care</SelectItem>
                  <SelectItem value="respite">Respite Care</SelectItem>
                  <SelectItem value="not-sure">Not Sure Yet</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="modal-urgency">How Soon Do You Need Care?</Label>
              <Select
                value={formData.urgency}
                onValueChange={(value) => handleChange("urgency", value)}
              >
                <SelectTrigger className="mt-1" data-testid="enquiry-urgency-select">
                  <SelectValue placeholder="Select timeframe" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="urgent">Urgent (within 1 week)</SelectItem>
                  <SelectItem value="soon">Soon (1-4 weeks)</SelectItem>
                  <SelectItem value="planning">Planning ahead (1-3 months)</SelectItem>
                  <SelectItem value="exploring">Just exploring options</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Message */}
          <div>
            <Label htmlFor="modal-message">Your Message</Label>
            <Textarea
              id="modal-message"
              data-testid="enquiry-message-input"
              value={formData.message}
              onChange={(e) => handleChange("message", e.target.value)}
              placeholder={getDefaultMessage() || "Tell us about your needs..."}
              rows={3}
              className="mt-1"
            />
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            data-testid="enquiry-submit-btn"
            disabled={isSubmitting}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white py-6 text-lg"
          >
            {isSubmitting ? (
              "Sending..."
            ) : (
              <>
                <Send className="h-5 w-5 mr-2" />
                {enquiryType === "brochure" ? "Request Brochure" : "Send Enquiry"}
              </>
            )}
          </Button>

          <p className="text-sm text-gray-500 text-center">
            We'll respond to your enquiry within 24 hours
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};
